import { css } from "@emotion/css";
import { Button, Callout, Divider } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { KubernetesTentacleEndpointResource, MachineModelHealthStatus, MachineResource, Repository } from "@octopusdeploy/octopus-server-client";
import { KubernetesMonitorStatus, MachineModelHealthStatusValues, ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { useAnalyticKubernetesMonitorInstallationStatusDispatch } from "~/analytics/Analytics";
import { DiscoveryProgress } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/DiscoveryProgress";
import { MonitorStatus } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/MonitorStatus";
import { contentContainerStyles } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import type { BaseConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { CodeEditor, type Language, type TextFormat } from "~/components/CodeEditor/CodeEditor";
import ExternalLink from "~/components/Navigation/ExternalLink/ExternalLink";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import Note from "~/primitiveComponents/form/Note/Note";
type InstallationPageProps<T extends MachineResource> = {
    model: BaseConfiguration;
    generateHelmCommand: (selectedLanguage: ScriptingLanguage | Language | TextFormat) => string;
    onAgentFound: (agent: T) => void;
    onAgentHealthStatusUpdated: (agent: T) => void;
    fetchAgent: (repository: Repository, name: string) => Promise<T | undefined>;
    hasTokenExpired: boolean;
    accessTokenExpiry: moment.Moment | undefined;
    machineType: EndpointSelectionScope;
};
export function InstallationPage<T extends MachineResource>(props: InstallationPageProps<T>) {
    const [hideTargetSearch, setHideTargetSearch] = useState(true);
    const [isFound, setIsFound] = useState(false);
    const [agentHealth, setAgentHealth] = useState<MachineModelHealthStatus>(MachineModelHealthStatusValues.Unknown);
    const [monitorEnabled, setMonitorEnabled] = useState(false);
    const [monitorStatus, setMonitorStatus] = useState<KubernetesMonitorStatus>(KubernetesMonitorStatus.NotRegistered);
    const [hideHelmCommand, setHideHelmCommand] = useState(false);
    const dispatchKubernetesMonitorInstallationStatus = useAnalyticKubernetesMonitorInstallationStatusDispatch();
    useEffect(() => {
        // Set a timeout to show the component after 2000 milliseconds (2 seconds)
        const timeoutId = setTimeout(() => setHideTargetSearch(false), 2000);
        // Clean up the timeout when the component unmounts
        return () => clearTimeout(timeoutId);
    }, []);
    const agent = useInlineStatusQuery(async (repository) => await props.fetchAgent(repository, props.model.Name), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], "Get Kubernetes target/worker details", {
        // Stop polling once we have the machine health status (no longer unknown)
        refetchIntervalInMs: isFound && agentHealth !== MachineModelHealthStatusValues.Unknown ? undefined : 2000,
    }).result;
    useEffect(() => {
        if (agent) {
            // Agent has been found
            if (!isFound) {
                setIsFound(true);
                setHideHelmCommand(true);
                props.onAgentFound(agent);
            }
            // This should run only once when the agent health status is first known
            if (agent.HealthStatus !== MachineModelHealthStatusValues.Unknown) {
                if (agentHealth !== agent.HealthStatus) {
                    setAgentHealth(agent.HealthStatus);
                    props.onAgentHealthStatusUpdated(agent);
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const k8sEndpoint = agent.Endpoint as KubernetesTentacleEndpointResource;
                    const newMonitorEnabled = k8sEndpoint.KubernetesAgentDetails?.KubernetesMonitorEnabled || false;
                    const newMonitorStatus = k8sEndpoint.KubernetesAgentDetails?.KubernetesMonitorStatus || KubernetesMonitorStatus.NotRegistered;
                    if (newMonitorEnabled) {
                        setMonitorEnabled(true);
                        setMonitorStatus(newMonitorStatus);
                    }
                    dispatchKubernetesMonitorInstallationStatus("Kubernetes Agent Installed", {
                        monitorEnabled: newMonitorEnabled,
                        monitorStatus: newMonitorStatus,
                        agentHealth: agent.HealthStatus,
                        agentName: props.model.Name,
                        storageClassName: props.model.StorageClassName,
                        machineType: props.machineType,
                    });
                }
            }
        }
    }, [agent, isFound, agentHealth, props, monitorEnabled, monitorStatus, dispatchKubernetesMonitorInstallationStatus]);
    const [selectedLanguage, setSelectedLanguage] = useState<ScriptingLanguage | Language | TextFormat>(ScriptingLanguage.Bash);
    const helmCommand = props.generateHelmCommand(selectedLanguage);
    const tokenExpiredAndHaveGivenUp = !isFound && props.hasTokenExpired;
    const expiryTimeInfo = !isFound && props.accessTokenExpiry && (<>
            Your token expires{" "}
            <b>
                <TimeFromNowLabel time={props.accessTokenExpiry.toISOString()}/>
            </b>
            .
        </>);
    const helmCommandLineCount = helmCommand.split(/\r\n|\r|\n/).length;
    //adjust the height of the code editor based on the number of lines in the helm command
    const codeEditorContainer = css({
        // 1.25rem per line (line height) and roughly 3rem of padding/margins/scrollbars
        minHeight: `calc((${helmCommandLineCount} * 1.25rem) + 3rem)`,
    });
    return (<div className={contentContainerStyles}>
            {!tokenExpiredAndHaveGivenUp && (<>
                    <p>We've generated a custom Helm command for you which includes a short-lived bearer token for Octopus Server. {expiryTimeInfo}</p>
                    <p>Copy and run this Helm command in a terminal connected to your Kubernetes cluster.</p>
                </>)}
            {tokenExpiredAndHaveGivenUp && (<Callout title={"Your bearer token has expired"} type={"danger"}>
                    <p>Regenerate the token and copy the updated Helm command.</p>
                </Callout>)}
            {isFound && (<div className="helmButtonContainer">
                    <Button importance="tertiary" label={hideHelmCommand ? "Show Helm command" : "Hide Helm command"} onClick={() => setHideHelmCommand(!hideHelmCommand)}></Button>
                </div>)}
            {!hideHelmCommand && (<>
                    <CodeEditor value={helmCommand} readOnly lineWrapping={false} showCopyButton showLineNumbers={false} language={selectedLanguage} allowFullScreen containerClassName={codeEditorContainer} settingsOverride={{ theme: "default", wordWrap: false }} onChange={noOp} scriptingLanguageSelectorOptions={{
                supportedLanguages: [ScriptingLanguage.Bash, ScriptingLanguage.PowerShell],
                onScriptingLanguageChanged: (syntax: ScriptingLanguage | Language | TextFormat) => setSelectedLanguage(syntax),
            }}/>
                    <Note>
                        Use of the {props.machineType === EndpointSelectionScope.Worker ? "worker" : "Kubernetes agent"} is subject to our <ExternalLink href={"https://octopus.com/company/legal"}>Customer Agreement</ExternalLink> with you.
                    </Note>
                </>)}
            {isFound && <Divider />}
            {!hideTargetSearch && !tokenExpiredAndHaveGivenUp && (<>
                    <DiscoveryProgress name={props.model.Name} isFound={isFound} healthStatus={agentHealth}/>
                    {monitorEnabled && <MonitorStatus healthStatus={monitorStatus} name={props.model.Name}/>}
                </>)}
        </div>);
}
